import React from 'react';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlus, faSpinner, faTrash} from '@fortawesome/free-solid-svg-icons';

import type {IProductionPlanSubStepProps} from '../productionInfo.types';

const faPlusIcon = faPlus as unknown as IconProp;
const faSpinnerIcon = faSpinner as unknown as IconProp;
const faTrashIcon = faTrash as unknown as IconProp;

export const ProductionPlanSubStep: React.FC<IProductionPlanSubStepProps> = ({
  selectedProductionPlanCropToAdd,
  addableProductionPlanCropOptions,
  productionPlanRows,
  productionPlanCropNameById,
  productionPlanTableErrors,
  productionPlanTableErrorMessage,
  productionPlanMonthOptions,
  productionPlanIrrigationOptions,
  isLoadingProductionPlans,
  isSavingProductionPlans,
  hasAttemptedProductionPlanLoad,
  onSelectedProductionPlanCropToAddChange,
  onAddProductionPlanCropRow,
  onUpdateProductionPlanRowField,
  onRemoveProductionPlanCropRow,
}) => {
  return (
    <div className="application-card production-plan-inline">
      <div className="production-plan-inline__header">
        <h3>Production plan</h3>
        <p>
          Capture and edit all crop production plan details directly in one
          table.
        </p>
      </div>

      <div className="production-plan-inline__toolbar">
        <div className="production-plan-inline__toolbar-actions">
          <label className="sr-only" htmlFor="plan-add-crop">
            Crop to add
          </label>
          <select
            id="plan-add-crop"
            className="production-plan-inline__add-crop-select"
            value={selectedProductionPlanCropToAdd}
            onChange={(event) =>
              onSelectedProductionPlanCropToAddChange(event.target.value)
            }
            disabled={
              isLoadingProductionPlans ||
              isSavingProductionPlans ||
              addableProductionPlanCropOptions.length === 0
            }
          >
            <option value="">Select crop</option>
            {addableProductionPlanCropOptions.map((crop) => (
              <option key={crop.id} value={crop.id}>
                {crop.name}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="money-btn money-btn--secondary money-btn--sm"
            onClick={onAddProductionPlanCropRow}
            disabled={
              isLoadingProductionPlans ||
              isSavingProductionPlans ||
              !selectedProductionPlanCropToAdd ||
              addableProductionPlanCropOptions.length === 0
            }
          >
            <FontAwesomeIcon icon={faPlusIcon} />
            <span>Add crop</span>
          </button>
        </div>

        <p>{productionPlanRows.length} crops prepared</p>
      </div>

      <div className="production-plan-inline__body" aria-live="polite">
        {isLoadingProductionPlans ? (
          <div className="production-plan-inline__loading" role="status">
            <FontAwesomeIcon icon={faSpinnerIcon} spin />
            <p>Loading production plan…</p>
          </div>
        ) : (
          <>
            {productionPlanTableErrorMessage ? (
              <div className="form-alert is-error" role="alert">
                {productionPlanTableErrorMessage}
              </div>
            ) : null}

            {productionPlanRows.length > 0 ? (
              <div className="production-plan-inline__table-wrap">
                <table className="production-plan-table">
                  <thead>
                    <tr>
                      <th scope="col">Crop</th>
                      <th scope="col">Hectares</th>
                      <th scope="col">Irrigation</th>
                      <th scope="col">Plant population</th>
                      <th scope="col">Estimated yield</th>
                      <th scope="col">Estimated price</th>
                      <th scope="col">Area irrigated</th>
                      <th scope="col">Area dryland</th>
                      <th scope="col">Plant month</th>
                      <th scope="col">Harvest month</th>
                      <th scope="col">Harvest year</th>
                      <th scope="col">Notes</th>
                      <th scope="col" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {productionPlanRows.map((row) => {
                      const rowErrors =
                        productionPlanTableErrors[row.rowId] || {};
                      const cropName =
                        productionPlanCropNameById.get(row.cropId) ||
                        'Unknown crop';
                      const hasRowErrors = Object.keys(rowErrors).length > 0;

                      return (
                        <tr
                          key={row.rowId}
                          className={hasRowErrors ? 'is-row-error' : undefined}
                        >
                          <td className="production-plan-table__crop-cell">
                            <span className="production-plan-table__crop-name">
                              {cropName}
                            </span>
                          </td>

                          <td>
                            <div className="production-plan-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`plan-hectares-${row.rowId}`}
                              >
                                Hectares for {cropName}
                              </label>
                              <input
                                id={`plan-hectares-${row.rowId}`}
                                className="production-plan-table__control"
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={row.numberOfHectares}
                                onChange={(event) =>
                                  onUpdateProductionPlanRowField(
                                    row.rowId,
                                    'numberOfHectares',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingProductionPlans}
                              />
                              {rowErrors.numberOfHectares ? (
                                <span className="production-plan-table__error">
                                  {rowErrors.numberOfHectares}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-plan-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`plan-irrigation-${row.rowId}`}
                              >
                                Irrigation for {cropName}
                              </label>
                              <select
                                id={`plan-irrigation-${row.rowId}`}
                                className="production-plan-table__control"
                                value={row.irrigation}
                                onChange={(event) =>
                                  onUpdateProductionPlanRowField(
                                    row.rowId,
                                    'irrigation',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingProductionPlans}
                              >
                                {productionPlanIrrigationOptions.map(
                                  (option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ),
                                )}
                              </select>
                            </div>
                          </td>

                          <td>
                            <div className="production-plan-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`plan-population-${row.rowId}`}
                              >
                                Plant population for {cropName}
                              </label>
                              <input
                                id={`plan-population-${row.rowId}`}
                                className="production-plan-table__control"
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={row.plantPopulation}
                                onChange={(event) =>
                                  onUpdateProductionPlanRowField(
                                    row.rowId,
                                    'plantPopulation',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingProductionPlans}
                              />
                              {rowErrors.plantPopulation ? (
                                <span className="production-plan-table__error">
                                  {rowErrors.plantPopulation}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-plan-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`plan-yield-${row.rowId}`}
                              >
                                Estimated yield for {cropName}
                              </label>
                              <input
                                id={`plan-yield-${row.rowId}`}
                                className="production-plan-table__control"
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={row.estimatedYield}
                                onChange={(event) =>
                                  onUpdateProductionPlanRowField(
                                    row.rowId,
                                    'estimatedYield',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingProductionPlans}
                              />
                              {rowErrors.estimatedYield ? (
                                <span className="production-plan-table__error">
                                  {rowErrors.estimatedYield}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-plan-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`plan-price-${row.rowId}`}
                              >
                                Estimated price for {cropName}
                              </label>
                              <input
                                id={`plan-price-${row.rowId}`}
                                className="production-plan-table__control"
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={row.estimatedPrice}
                                onChange={(event) =>
                                  onUpdateProductionPlanRowField(
                                    row.rowId,
                                    'estimatedPrice',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingProductionPlans}
                              />
                              {rowErrors.estimatedPrice ? (
                                <span className="production-plan-table__error">
                                  {rowErrors.estimatedPrice}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-plan-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`plan-irrigated-${row.rowId}`}
                              >
                                Area irrigated for {cropName}
                              </label>
                              <input
                                id={`plan-irrigated-${row.rowId}`}
                                className="production-plan-table__control"
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={row.areaUnderIrrigation}
                                onChange={(event) =>
                                  onUpdateProductionPlanRowField(
                                    row.rowId,
                                    'areaUnderIrrigation',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingProductionPlans}
                              />
                              {rowErrors.areaUnderIrrigation ? (
                                <span className="production-plan-table__error">
                                  {rowErrors.areaUnderIrrigation}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-plan-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`plan-dryland-${row.rowId}`}
                              >
                                Area dryland for {cropName}
                              </label>
                              <input
                                id={`plan-dryland-${row.rowId}`}
                                className="production-plan-table__control"
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={row.areaOfDryLand}
                                onChange={(event) =>
                                  onUpdateProductionPlanRowField(
                                    row.rowId,
                                    'areaOfDryLand',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingProductionPlans}
                              />
                              {rowErrors.areaOfDryLand ? (
                                <span className="production-plan-table__error">
                                  {rowErrors.areaOfDryLand}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-plan-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`plan-plant-month-${row.rowId}`}
                              >
                                Plant month for {cropName}
                              </label>
                              <select
                                id={`plan-plant-month-${row.rowId}`}
                                className="production-plan-table__control"
                                value={row.plantingMonth}
                                onChange={(event) =>
                                  onUpdateProductionPlanRowField(
                                    row.rowId,
                                    'plantingMonth',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingProductionPlans}
                              >
                                <option value="">Select month</option>
                                {productionPlanMonthOptions.map((month) => (
                                  <option key={month.value} value={month.value}>
                                    {month.label}
                                  </option>
                                ))}
                              </select>
                              {rowErrors.plantingMonth ? (
                                <span className="production-plan-table__error">
                                  {rowErrors.plantingMonth}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-plan-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`plan-harvest-month-${row.rowId}`}
                              >
                                Harvest month for {cropName}
                              </label>
                              <select
                                id={`plan-harvest-month-${row.rowId}`}
                                className="production-plan-table__control"
                                value={row.harvestMonth}
                                onChange={(event) =>
                                  onUpdateProductionPlanRowField(
                                    row.rowId,
                                    'harvestMonth',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingProductionPlans}
                              >
                                <option value="">Select month</option>
                                {productionPlanMonthOptions.map((month) => (
                                  <option key={month.value} value={month.value}>
                                    {month.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </td>

                          <td>
                            <div className="production-plan-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`plan-harvest-year-${row.rowId}`}
                              >
                                Harvest year for {cropName}
                              </label>
                              <input
                                id={`plan-harvest-year-${row.rowId}`}
                                className="production-plan-table__control"
                                type="number"
                                min="2000"
                                step="1"
                                value={row.harvestYear}
                                onChange={(event) =>
                                  onUpdateProductionPlanRowField(
                                    row.rowId,
                                    'harvestYear',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingProductionPlans}
                              />
                              {rowErrors.harvestYear ? (
                                <span className="production-plan-table__error">
                                  {rowErrors.harvestYear}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-plan-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`plan-notes-${row.rowId}`}
                              >
                                Notes for {cropName}
                              </label>
                              <textarea
                                id={`plan-notes-${row.rowId}`}
                                className="production-plan-table__control production-plan-table__control--notes"
                                rows={3}
                                value={row.notes}
                                onChange={(event) =>
                                  onUpdateProductionPlanRowField(
                                    row.rowId,
                                    'notes',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingProductionPlans}
                              />
                              {rowErrors.general ? (
                                <span className="production-plan-table__error">
                                  {rowErrors.general}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td className="production-plan-table__actions">
                            <button
                              type="button"
                              className="money-btn money-btn--danger money-btn--sm"
                              onClick={() =>
                                onRemoveProductionPlanCropRow(row.cropId)
                              }
                              disabled={isSavingProductionPlans}
                              title={`Remove ${cropName}`}
                            >
                              <FontAwesomeIcon icon={faTrashIcon} />
                              <span className="sr-only">Remove {cropName}</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : hasAttemptedProductionPlanLoad ? (
              <div className="production-plan-inline__empty">
                <h3>No production plan captured yet</h3>
                <p>
                  Add a crop and start filling in plan values directly in the
                  table.
                </p>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
